import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Send, AtSign } from "lucide-react";
import { cn } from "@/lib/utils";

interface ComposerProps {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  canSend: boolean;
  isSending?: boolean;
  placeholder?: string;
  brains?: Array<{ id: string; name: string; color?: string }>;
  onMentionBrain?: (brainId: string) => void;
  currentMode?: "chat" | "debate" | "autonomous";
}

export function Composer({
  value,
  onChange,
  onSend,
  canSend,
  isSending,
  placeholder = "Type a message…",
  brains = [],
  onMentionBrain,
  currentMode = "chat",
}: ComposerProps) {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const namedBrainsCount = (brains || []).filter(b => (b?.name || '').trim().length > 0).length;
  const getSmartPlaceholder = () => {
    if (namedBrainsCount > 1) return `${placeholder} Use @brain to mention`;
    return placeholder;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    onChange(newValue);
    const beforeCursor = newValue.slice(0, cursorPos);
    const atMatch = beforeCursor.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setShowMentions(true);
      setSelectedMentionIndex(0);
    } else {
      setShowMentions(false);
    }
  };

  const filteredBrains = (brains || [])
    .filter(b => !!(b?.name && b.name.trim()))
    .filter(b => (b.name || '').toLowerCase().includes(mentionQuery.toLowerCase()))
    .slice(0, 8);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showMentions && filteredBrains.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(i => Math.min(i + 1, filteredBrains.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        handleBrainSelect(filteredBrains[selectedMentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey && !showMentions) {
      e.preventDefault();
      onSend();
    }
  };

  const handleBrainSelect = (brain: { id: string; name: string }) => {
    if (!inputRef.current) return;
    const cursorPos = inputRef.current.selectionStart || 0;
    const before = value.slice(0, cursorPos);
    const match = before.match(/^(.*)@(\w*)$/);
    const beforeAt = match ? match[1] : before;
    const afterCursor = value.slice(cursorPos);
    const newValue = beforeAt + `@${brain.name} ` + afterCursor;
    onChange(newValue);
    setShowMentions(false);
    if (onMentionBrain) onMentionBrain(brain.id);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const newPos = beforeAt.length + brain.name.length + 2;
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const insertAt = () => {
    if (!inputRef.current) return;
    const pos = inputRef.current.selectionStart || 0;
    const newValue = value.slice(0, pos) + '@' + value.slice(pos);
    onChange(newValue);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(pos + 1, pos + 1);
      }
    }, 0);
  };

  return (
    <div
      className="
        shrink-0 bg-background/95 backdrop-blur
        border-t border-ios-separator px-4 py-3
      "
      style={{ marginBottom: `calc(var(--tabbar-h) + var(--safe-bottom) + var(--tabbar-gap))` }}
    >
      <div className="flex items-end gap-3">
        <div className="flex-1 flex items-center gap-2 bg-input rounded-2xl px-4 py-2 relative">
          {namedBrainsCount > 1 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={insertAt}
              className="w-8 h-8 rounded-full text-muted-foreground hover:text-foreground shrink-0"
              title="Mention a brain"
            >
              <AtSign size={16} />
            </Button>
          )}
          <textarea
            ref={inputRef}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={getSmartPlaceholder()}
            className="w-full bg-transparent text-foreground placeholder:text-muted-foreground outline-none resize-none ios-body min-h-[1.5rem] max-h-32"
            rows={1}
            style={{ height: 'auto', minHeight: '1.5rem' }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = `${Math.min(target.scrollHeight, 128)}px`;
            }}
          />

          {showMentions && filteredBrains.length > 0 && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-card border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto z-50">
              <div className="p-2">
                <div className="text-xs text-muted-foreground mb-2 px-2">Mention a brain:</div>
                {filteredBrains.map((b, i) => (
                  <button
                    key={b.id}
                    onClick={() => handleBrainSelect(b)}
                    className={cn(
                      "w-full px-3 py-2 text-left hover:bg-accent flex items-center gap-3 rounded-lg transition-colors",
                      i === selectedMentionIndex && "bg-accent"
                    )}
                  >
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium"
                      style={{ backgroundColor: b.color || "#6366f1" }}
                    >
                      {(b.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{b.name || 'Unnamed Brain'}</div>
                      <div className="text-xs text-muted-foreground">@{b.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Mode emoji badge removed for cleaner input chrome */}
        </div>
        <Button
          onClick={onSend}
          disabled={!canSend}
          size="sm"
          className={cn(
            "rounded-full w-10 h-10 p-0 transition-all",
            canSend ? "bg-ios-blue hover:bg-ios-blue/90 text-white" : "bg-muted text-muted-foreground"
          )}
        >
          {isSending ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Send size={16} />}
        </Button>
      </div>
    </div>
  );
}
